// 盤面資料模型 — 內部邏輯用的簡化表示，概念上對應 docs/PROTOBUF_SCHEMA.md 的
// GameSymbol{ Reels: ReelSymbol{ Symbols: CellSymbol{Symbol,Size} }[] }。
//
// 跨格大符號（長條形符號，Megaways 風格）以「網格 + size 標記」表示（對照 Unity CellData.Size）：
//   - size 省略或 1：一般單格符號。
//   - size >= 2：長條符號的「頂格」，往下佔用 size 格（其符號 id 仍寫在此格）。
//   - size === 0：被上方長條符號覆蓋的「延續格」，symbol 與該長條符號相同（讓 ways/消除自然以整顆處理），
//     但畫面不獨立繪製這一格（由頂格的長條 SymbolView 一次涵蓋）。
// 這樣既保留固定網格（tumble/ways 引擎與現有測試不變），又能表現整顆長條符號。

export interface BoardCell {
  readonly symbol: number
  /** 跨格高度：省略/1=一般格、>=2=長條符號頂格、0=被覆蓋的延續格。見檔頭說明。 */
  readonly size?: number
  /**
   * 顯示用符號 id（對應 Unity CellData.ViewSymbolId）：決定「畫哪套美術」。
   * 一般格省略（＝symbol）；當角色符號（H2/H3/H4）變身為 Wild 時，symbol 變 WILD(0) 但 viewSymbolId
   * 保留原符號 id，讓畫面顯示角色專屬百搭造型（W2/W3/W4）而非通用 WW，且此值隨符號下墜（{...cell} 複製）
   * 跨連鎖持久保留（對照 Unity SetWild 只改 SymbolId、不動 ViewSymbolId）。
   */
  readonly viewSymbolId?: number
}

/** 此格代表的長條高度（頂格回傳 size、一般格回傳 1、被覆蓋的延續格回傳 0）。 */
export function cellSpan(cell: BoardCell): number {
  return cell.size ?? 1
}

/** 此格的顯示用符號 id（viewSymbolId 省略時＝symbol）。見 BoardCell.viewSymbolId 說明。 */
export function cellViewSymbol(cell: BoardCell): number {
  return cell.viewSymbolId ?? cell.symbol
}

/** 此格是否為被上方長條符號覆蓋的延續格（不獨立繪製）。 */
export function isCovered(cell: BoardCell): boolean {
  return cell.size === 0
}

/** 一條輪帶由上到下的格子。 */
export type ReelColumn = readonly BoardCell[]

/** 主盤面：index 0-5 對應主輪，不含 Tracker 頂輪（頂輪另用獨立的 ReelColumn 表示）。 */
export type Board = readonly ReelColumn[]

export interface CellRef {
  readonly reel: number
  readonly row: number
}

export function cellAt(board: Board, ref: CellRef): BoardCell {
  const column = board[ref.reel]
  const cell = column?.[ref.row]
  if (cell === undefined) {
    throw new Error(`盤面座標超出範圍: reel=${ref.reel}, row=${ref.row}`)
  }
  return cell
}
